import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, Lightbulb } from 'lucide-react';
import { crearAccion } from '../../../api/accionesPersonalApi';
import { getEmpleados, getDepartamentos, getPuestos } from '../../../api/catalogosApi';
import FormularioIngreso from './FormularioIngreso'; // ← componente con búsqueda de postulantes
import SearchableSelect from '../../shared/SearchableSelect';

const NuevaAccion = ({ onClose, onSuccess }) => {
  const [tipoAccion, setTipoAccion] = useState('');
  const [formData, setFormData] = useState({
    empleado_id: '',
    tipo_accion: '',
    fecha_efectiva: '',
    departamento_nuevo: '',
    puesto_nuevo: '',
    salario_nuevo: '',
    justificacion: '',
    supervisor_nuevo: '',
    datos_especificos: {}
  });

  const [empleados,      setEmpleados]      = useState([]);
  const [departamentos,  setDepartamentos]  = useState([]);
  const [puestos,        setPuestos]        = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState('');
  const [cargandoDatos,  setCargandoDatos]  = useState(true);

  useEffect(() => { cargarDatosIniciales(); }, []);

  const cargarDatosIniciales = async () => {
    try {
      setCargandoDatos(true);
      const [dataEmpleados, dataDepartamentos, dataPuestos] = await Promise.all([
        getEmpleados(),
        getDepartamentos(),
        getPuestos()
      ]);
      if (dataEmpleados.success   && Array.isArray(dataEmpleados.empleados))       setEmpleados(dataEmpleados.empleados);
      if (dataDepartamentos.success && Array.isArray(dataDepartamentos.departamentos)) setDepartamentos(dataDepartamentos.departamentos);
      if (dataPuestos.success     && Array.isArray(dataPuestos.puestos))           setPuestos(dataPuestos.puestos);
    } catch (err) {
      console.error('❌ Error al cargar datos:', err);
      setError('Error al cargar los datos necesarios');
    } finally {
      setCargandoDatos(false);
    }
  };

  const handleTipoChange = (e) => {
    const val = e.target.value;
    setTipoAccion(val);
    setFormData(prev => ({ ...prev, tipo_accion: val, empleado_id: '' }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const obtenerEmpleadoSeleccionado = () =>
    empleados.find(emp => emp.EmpleadoID === parseInt(formData.empleado_id));

  const construirDatosAnteriores = () => {
    const empleado = obtenerEmpleadoSeleccionado();
    if (!empleado) return {};
    switch (formData.tipo_accion) {
      case 'PROMOCION': case 'CAMBIO_PUESTO':
        return { cargo: empleado.CARGO || null, salario: empleado.Salario || null, departamento: empleado.DEPARTAMENTOID || null };
      case 'AJUSTE_SALARIAL':
        return { salario: empleado.Salario || null };
      case 'CAMBIO_DEPTO':
        return { departamento: empleado.DEPARTAMENTOID || null, supervisor: empleado.SupervisorID || null };
      case 'CAMBIO_SUPERVISOR':
        return { supervisor: empleado.SupervisorID || null };
      case 'CAMBIO_JORNADA': case 'CAMBIO_TURNO':
        return { jornada: empleado.Jornada || null, turno: empleado.Turno || null, salario: empleado.Salario || null };
      case 'RENOVACION_CONTRATO': case 'CAMBIO_TIPO_CONTRATO':
        return { tipoContrato: empleado.TipoContrato || null, fechaVencimiento: empleado.FechaVencimientoContrato || null };
      default: return {};
    }
  };

  const construirDatosEspecificos = () => {
    const datos = {};
    switch (formData.tipo_accion) {
      case 'PROMOCION':
        if (formData.puesto_nuevo)       datos.nuevoCargo        = formData.puesto_nuevo;
        if (formData.salario_nuevo)      datos.nuevoSalario      = formData.salario_nuevo;
        if (formData.departamento_nuevo) datos.nuevoDepartamento = formData.departamento_nuevo;
        break;
      case 'AJUSTE_SALARIAL':
        if (formData.salario_nuevo) datos.nuevoSalario = formData.salario_nuevo;
        break;
      case 'CAMBIO_DEPTO':
        if (formData.departamento_nuevo) datos.nuevoDepartamento = formData.departamento_nuevo;
        if (formData.supervisor_nuevo)   datos.nuevoSupervisor   = formData.supervisor_nuevo;
        break;
      case 'CAMBIO_SUPERVISOR':
        if (formData.supervisor_nuevo) datos.nuevoSupervisorId = parseInt(formData.supervisor_nuevo);
        break;
      case 'CAMBIO_PUESTO':
        if (formData.puesto_nuevo) datos.nuevoCargo = formData.puesto_nuevo;
        break;
      case 'TERMINACION':
        datos.fechaSalida  = formData.fecha_efectiva;
        datos.motivoSalida = formData.justificacion;
        break;
      default: break;
    }
    return datos;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Los selects nativos con "required" se reemplazaron por SearchableSelect
    // (que no soporta la validacion HTML5 required), asi que estos casos se
    // validan aqui explicitamente para no perder la garantia que tenian antes.
    if (!formData.empleado_id) {
      setError('Debe seleccionar un empleado');
      return;
    }
    if (tipoAccion === 'CAMBIO_SUPERVISOR' && !formData.supervisor_nuevo) {
      setError('Debe seleccionar el nuevo supervisor');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const resultado = await crearAccion({
        tipoAccion:      formData.tipo_accion,
        empleadoId:      parseInt(formData.empleado_id),
        fechaEfectiva:   formData.fecha_efectiva,
        justificacion:   formData.justificacion,
        datosAnteriores: construirDatosAnteriores(),
        datosNuevos:     construirDatosEspecificos()
      });
      if (resultado.success) { onSuccess(); onClose(); }
      else setError(resultado.message || 'Error al crear la acción');
    } catch (err) {
      console.error('Error:', err);
      setError('Error al crear la acción de personal');
    } finally {
      setLoading(false);
    }
  };

  const mostrarCamposSalario      = () => ['AJUSTE_SALARIAL', 'PROMOCION'].includes(tipoAccion);
  const mostrarCamposDepartamento = () => ['CAMBIO_DEPTO', 'PROMOCION', 'TRANSFERENCIA'].includes(tipoAccion);
  const mostrarCamposPuesto       = () => ['CAMBIO_PUESTO', 'PROMOCION'].includes(tipoAccion);
  const mostrarCampoSupervisor    = () => ['CAMBIO_SUPERVISOR', 'CAMBIO_DEPTO'].includes(tipoAccion);

  const labelStyle = { display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' };
  const inputStyle = { width: '100%', padding: '0.625rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' };

  /* ══════════════════════════════════════════════════════
     INGRESO → delegar completamente a FormularioIngreso
  ══════════════════════════════════════════════════════ */
  if (tipoAccion === 'INGRESO') {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '1rem', overflow: 'auto'
      }}>
        <div style={{
          background: 'white', borderRadius: '12px',
          maxWidth: '860px', width: '100%',
          maxHeight: '92vh', overflow: 'hidden',
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
        }}>
          {/* Header */}
          <div style={{
            padding: '1.25rem 1.5rem', borderBottom: '1px solid #e5e7eb',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: 'white'
          }}>
            <div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>
                Nueva Acción — Ingreso de Personal
              </h2>
              <p style={{ fontSize: '0.78rem', margin: '0.2rem 0 0', opacity: 0.85 }}>
                Ingreso manual o desde postulante del portal de empleos
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <button
                type="button"
                onClick={() => setTipoAccion('')}
                style={{
                  padding: '0.4rem 0.9rem',
                  background: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  borderRadius: '6px', color: 'white',
                  fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer'
                }}
              >
                ← Cambiar tipo
              </button>
              <button onClick={onClose} style={{
                background: 'rgba(255,255,255,0.2)', border: 'none',
                borderRadius: '6px', padding: '0.5rem',
                cursor: 'pointer', color: 'white', lineHeight: 0
              }}>
                <X size={22} />
              </button>
            </div>
          </div>

          {/* FormularioIngreso con scroll */}
          <div style={{ overflowY: 'auto', maxHeight: 'calc(92vh - 78px)', padding: '1.5rem' }}>
            <FormularioIngreso
              tipo="INGRESO"
              onClose={onClose}
              onSuccess={onSuccess}
            />
          </div>
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════
     PANTALLA INICIAL: solo el selector de tipo
  ══════════════════════════════════════════════════════ */
  if (!tipoAccion) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '1rem'
      }}>
        <div style={{
          background: 'white', borderRadius: '12px',
          maxWidth: '460px', width: '100%',
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', overflow: 'hidden'
        }}>
          <div style={{
            padding: '1.5rem',
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>Nueva Acción de Personal</h2>
            <button onClick={onClose} style={{
              background: 'rgba(255,255,255,0.2)', border: 'none',
              borderRadius: '6px', padding: '0.5rem', cursor: 'pointer', color: 'white', lineHeight: 0
            }}>
              <X size={22} />
            </button>
          </div>

          <div style={{ padding: '1.5rem' }}>
            <label style={labelStyle}>Tipo de Acción <span style={{ color: '#ef4444' }}>*</span></label>
            <select value={tipoAccion} onChange={handleTipoChange} autoFocus style={{ ...inputStyle, marginBottom: '0.75rem' }}>
              <option value="">Seleccione el tipo</option>
              <optgroup label="Ingresos">
                <option value="INGRESO">Ingreso de Personal</option>
                <option value="REINGRESO">Reingreso</option>
              </optgroup>
              <optgroup label="Movimientos">
                <option value="PROMOCION">Promoción</option>
                <option value="CAMBIO_DEPTO">Cambio de Departamento</option>
                <option value="CAMBIO_PUESTO">Cambio de Puesto</option>
                <option value="AJUSTE_SALARIAL">Ajuste Salarial</option>
                <option value="TRANSFERENCIA">Transferencia</option>
                <option value="CAMBIO_SUPERVISOR">Cambio de Supervisor</option>
                <option value="CAMBIO_JORNADA">Cambio de Jornada</option>
              </optgroup>
              <optgroup label="Egresos">
                <option value="TERMINACION">Terminación</option>
              </optgroup>
            </select>

            <p style={{ fontSize: '0.78rem', color: '#6b7280', margin: 0 }}>
              <Lightbulb size={14} style={{ verticalAlign:'-2px', marginRight:'0.35rem', flexShrink:0 }} />El ingreso de personal permite cargar datos automáticamente desde el portal de empleos.
            </p>
          </div>
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════
     FORMULARIO para todos los demás tipos
  ══════════════════════════════════════════════════════ */
  if (cargandoDatos) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
      }}>
        <div style={{ background: 'white', borderRadius: '12px', padding: '3rem', textAlign: 'center' }}>
          <div style={{
            width: '50px', height: '50px', border: '4px solid #e5e7eb',
            borderTop: '4px solid #3b82f6', borderRadius: '50%',
            animation: 'spin 1s linear infinite', margin: '0 auto 1rem'
          }} />
          <p style={{ color: '#6b7280' }}>Cargando formulario...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '1rem', overflow: 'auto'
    }}>
      <div style={{
        background: 'white', borderRadius: '12px',
        maxWidth: '800px', width: '100%', maxHeight: '90vh', overflow: 'hidden',
        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem', borderBottom: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: 'white'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>Nueva Acción de Personal</h2>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              type="button" onClick={() => setTipoAccion('')}
              style={{
                padding: '0.4rem 0.9rem',
                background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: '6px', color: 'white', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer'
              }}
            >
              ← Cambiar tipo
            </button>
            <button onClick={onClose} style={{
              background: 'rgba(255,255,255,0.2)', border: 'none',
              borderRadius: '6px', padding: '0.5rem', cursor: 'pointer', color: 'white', lineHeight: 0
            }}>
              <X size={24} />
            </button>
          </div>
        </div>

        {error && (
          <div style={{
            margin: '1rem 1.5rem', padding: '1rem',
            background: '#fee2e2', border: '1px solid #fecaca',
            borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem'
          }}>
            <AlertCircle size={20} color="#dc2626" />
            <span style={{ color: '#dc2626', fontSize: '0.875rem' }}>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ overflowY: 'auto', maxHeight: 'calc(90vh - 140px)' }}>
          <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

            {/* Tipo (solo lectura) */}
            <div style={{
              padding: '0.75rem 1rem', background: '#eff6ff',
              border: '1px solid #bfdbfe', borderRadius: '8px',
              fontSize: '0.875rem', color: '#1e40af', fontWeight: '600'
            }}>
              Tipo seleccionado: {formData.tipo_accion}
            </div>

            {/* Empleado */}
            <div>
              <label style={labelStyle}>Empleado <span style={{ color: '#ef4444' }}>*</span></label>
              <SearchableSelect
                value={formData.empleado_id}
                onChange={v => setFormData(prev => ({ ...prev, empleado_id: v }))}
                options={empleados.map(emp => ({ id: emp.EmpleadoID, label: `${emp.NOMBRE} ${emp.APELLIDO} — ${emp.CEDULA}` }))}
                placeholder="Buscar empleado..."
                emptyLabel="Seleccione un empleado"
                allowEmpty={false}
                inputStyle={inputStyle}
              />
            </div>

            {formData.empleado_id && obtenerEmpleadoSeleccionado() && (
              <div style={{
                padding: '0.875rem 1rem', background: '#f0f9ff',
                borderRadius: '8px', border: '1px solid #bfdbfe',
                fontSize: '0.875rem', color: '#1e40af'
              }}>
                <strong>Cargo Actual:</strong> {obtenerEmpleadoSeleccionado().CARGO || 'N/A'}
                {obtenerEmpleadoSeleccionado().Salario && (
                  <> &nbsp;·&nbsp; <strong>Salario:</strong> RD$ {parseFloat(obtenerEmpleadoSeleccionado().Salario).toLocaleString('es-DO')}</>
                )}
              </div>
            )}

            {/* Fecha efectiva */}
            <div>
              <label style={labelStyle}>Fecha Efectiva <span style={{ color: '#ef4444' }}>*</span></label>
              <input type="date" name="fecha_efectiva" value={formData.fecha_efectiva} onChange={handleChange} required style={inputStyle} />
            </div>

            {/* Campos condicionales */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              {mostrarCamposDepartamento() && (
                <div>
                  <label style={labelStyle}>Nuevo Departamento</label>
                  <SearchableSelect
                    value={formData.departamento_nuevo}
                    onChange={v => setFormData(prev => ({ ...prev, departamento_nuevo: v }))}
                    options={departamentos.map(d => ({ id: d.DepartamentoID, label: d.Nombre }))}
                    placeholder="Buscar departamento..."
                    emptyLabel="Seleccione"
                    inputStyle={inputStyle}
                  />
                </div>
              )}
              {mostrarCampoSupervisor() && (
                <div>
                  <label style={labelStyle}>Nuevo Supervisor <span style={{ color: '#ef4444' }}>*</span></label>
                  <SearchableSelect
                    value={formData.supervisor_nuevo}
                    onChange={v => setFormData(prev => ({ ...prev, supervisor_nuevo: v }))}
                    options={empleados.map(emp => ({ id: emp.EmpleadoID, label: `${emp.NOMBRE} ${emp.APELLIDO}` }))}
                    placeholder="Buscar supervisor..."
                    emptyLabel="Seleccione"
                    inputStyle={inputStyle}
                  />
                </div>
              )}
              {mostrarCamposPuesto() && (
                <div>
                  <label style={labelStyle}>Nuevo Puesto</label>
                  <SearchableSelect
                    value={formData.puesto_nuevo}
                    onChange={v => setFormData(prev => ({ ...prev, puesto_nuevo: v }))}
                    options={puestos.map(p => ({ id: p.id, label: p.nombre }))}
                    placeholder="Buscar puesto..."
                    emptyLabel="Seleccione"
                    inputStyle={inputStyle}
                  />
                </div>
              )}
              {mostrarCamposSalario() && (
                <div>
                  <label style={labelStyle}>Nuevo Salario (RD$)</label>
                  <input type="number" name="salario_nuevo" value={formData.salario_nuevo} onChange={handleChange}
                    step="0.01" min="0" placeholder="0.00" required={tipoAccion === 'AJUSTE_SALARIAL'} style={inputStyle} />
                </div>
              )}
            </div>

            {/* Justificación */}
            <div>
              <label style={labelStyle}>Justificación <span style={{ color: '#ef4444' }}>*</span></label>
              <textarea name="justificacion" value={formData.justificacion} onChange={handleChange}
                required rows={4} placeholder="Describa la justificación de esta acción..."
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
            </div>
          </div>

          {/* Footer */}
          <div style={{
            padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb',
            display: 'flex', justifyContent: 'flex-end', gap: '1rem', background: '#f9fafb'
          }}>
            <button type="button" onClick={onClose} disabled={loading} style={{
              padding: '0.625rem 1.25rem', background: 'white',
              border: '1px solid #d1d5db', borderRadius: '8px',
              fontSize: '0.875rem', fontWeight: '600', color: '#374151',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}>
              Cancelar
            </button>
            <button type="submit" disabled={loading} style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.625rem 1.25rem',
              background: loading ? '#9ca3af' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              border: 'none', borderRadius: '8px',
              fontSize: '0.875rem', fontWeight: '600', color: 'white',
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: loading ? 'none' : '0 4px 6px rgba(59,130,246,0.3)'
            }}>
              {loading ? (
                <>
                  <div style={{ width: '16px', height: '16px', border: '2px solid white', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  Guardando...
                </>
              ) : (
                <><Save size={18} />Crear Acción</>
              )}
            </button>
          </div>
        </form>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default NuevaAccion;